/**
 * Agent Response Queue — boundary contract schemas.
 *
 * Owned here, not beside the route: `apps/api/src/routes/manager/agent-response-queue.routes.ts`
 * registers these schemas, and `apps/landlord/src/pages/AgentResponseQueuePage.tsx` derives
 * its response types from the same objects. See packages/api-types/README.md.
 *
 * Covers:
 *   GET  /agent-response-queue?status=pending_review
 *   POST /agent-response-queue/:id/approve
 *   POST /agent-response-queue/:id/reject
 *   POST /agent-response-queue/:id/replace
 */
import { z } from 'zod';
import { qualityReviewTierSchema } from './escalation-queue.js';
// ---------------------------------------------------------------------------
// Queue list query
// ---------------------------------------------------------------------------
export const queueListQuerySchema = z
    .object({
    status: z
        .enum(['pending_review'])
        .optional()
        .default('pending_review')
        .describe('Filter by review status (currently only pending_review is supported)'),
})
    .describe('Agent response queue list query parameters');
// ---------------------------------------------------------------------------
// Queue item shape returned in GET response
// Fields defined by the frozen boundary contract (produces).
// ---------------------------------------------------------------------------
export const queueItemSchema = z
    .object({
    id: z.uuid().describe('AgentResponseReviewLog ID'),
    caseId: z.uuid().describe('Associated case ID'),
    /** agentKindId mapped to agentKind (frozen boundary field name) */
    agentKind: z.string().describe('Agent kind ID (UUID) that authored this draft'),
    /** RA-QUEUE-LINK-FIX — same field name/shape as EscalationListItemSchema.agentKindSlug */
    agentKindSlug: z.string().describe('Slug of the AgentKind that authored this draft'),
    /** BR-ESC-QUEUE-REACH — same meaning as EscalationListItemSchema.leadId */
    leadId: z
        .uuid()
        .nullable()
        .optional()
        .describe('Originating leasing Lead ID — the lead itself for a lead case, or the lead a reference check was requested on for a reference-check case; null for any other (e.g. repair) case'),
    recipientType: z.string().describe('Intended recipient type (tenant|manager|vendor|prospect)'),
    proposedContent: z.string().describe('Agent-drafted message text (PII)'),
    status: z
        .enum(['pending_review', 'approved', 'rejected', 'replaced'])
        .describe('Current hold status'),
    createdAt: z.string().describe('ISO 8601 creation timestamp'),
    ageMs: z.number().int().describe('Milliseconds since this hold was created'),
    forcedByReviewer: z.boolean().describe('BR-QR-HOLD: true when a runtime quality reviewer forced this hold'),
    reviewerCritique: z.string().nullable().describe('Reviewer critique of the draft (PII); null when not reviewer-forced'),
    reviewerItemId: z.string().nullable().describe('Quality-catalog / playbook item id the critique cites'),
    reviewerItemText: z.string().nullable().describe('Text of the cited item (PII)'),
    reviewerTier: qualityReviewTierSchema.nullable().describe('Tier of the cited item'),
})
    .describe('A single agent response review queue item');
// ---------------------------------------------------------------------------
// Queue list response
// ---------------------------------------------------------------------------
export const queueListResponseSchema = z
    .object({
    items: z.array(queueItemSchema).describe('Pending review items for this manager'),
})
    .describe('Agent response review queue list response');
// ---------------------------------------------------------------------------
// Resolve endpoint path param
// ---------------------------------------------------------------------------
export const resolveIdParamsSchema = z
    .object({
    id: z.uuid().describe('AgentResponseReviewLog ID to resolve'),
})
    .describe('Review log ID path parameter');
// ---------------------------------------------------------------------------
// Replace body — replacementContent is required
// ---------------------------------------------------------------------------
export const replaceBodySchema = z
    .object({
    replacementContent: z
        .string()
        .min(1, 'Replacement content must not be empty')
        .max(4000, 'Replacement content must be 4000 characters or less')
        .describe('Manager-authored replacement message text'),
})
    .describe('Replace request body (BR-RESOLVE-REPLACE)');
// ---------------------------------------------------------------------------
// Resolve response — echoes the resolution status
// ---------------------------------------------------------------------------
export const resolveResponseSchema = z
    .object({
    status: z
        .enum(['approved', 'rejected', 'replaced'])
        .describe('Resolution status applied to the review hold'),
})
    .describe('Agent response review resolution response');
//# sourceMappingURL=response-queue.js.map